import React, { useState } from "react";
import {
  Title,
  GoBack,
  Message,
  TextArea,
  InputField,
  ActionButton,
} from "../../component/Component";
import { codeOk, errorMessage } from "../../../../middlewares/index";
import { useLanguageContext } from "../../../../context/language/LanguageContext";

const Add = () => {
  const { createOneLanguage } = useLanguageContext();

  const [error, setError] = useState(false);
  const [status, setStatus] = useState(0);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState(null);
  const [description, setDescription] = useState(null);

  const languageRoutes = "/language";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setName(null);
    setDescription(null);
  };

  const responses = (isError, _status, _message) => {
    setError(isError);
    setStatus(_status);
    setErrorMessage(_message);
  };

  const addOneLanguageHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = { name, description };
    const response = await createOneLanguage(data);

    if (response) {
      if (response.data.status === codeOk) {
        cleanFields();
        return responses(true, response.data.status, response.data.message);
      } else {
        setLoading(false);
        return responses(true, response.data.status, response.data.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 lg:p-6">
      <div className="mx-auto max-w-4xl">
        {/* Top bar */}
        <div className="mb-4 flex items-center justify-between">
          <GoBack props={{ route: languageRoutes }} />
          <span className="hidden rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 sm:inline-flex">
            New entry
          </span>
        </div>

        {/* Alerts */}
        <div className="mb-3">
          <Message
            props={{
              error,
              success,
              message,
              loading,
              status,
              onClick: () => {
                setError(false);
                setSuccess(false);
                setErrorMessage("");
              },
            }}
          />
        </div>

        {/* Card */}
        <form onSubmit={addOneLanguageHandler}>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-indigo-100 text-indigo-600">
                  {/* translate icon */}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M4 4h8a1 1 0 0 1 0 2H6.41l1.3 1.3A9 9 0 0 0 11 9.9a9.9 9.9 0 0 0 2.27-3.1 1 1 0 1 1 1.79.9A12 12 0 0 1 13 10.91a12.5 12.5 0 0 1-2 .9 10.6 10.6 0 0 0 3.24 3.27l.49-.49a1 1 0 1 1 1.41 1.41l-.49.49a10.7 10.7 0 0 0 1.61 1.11 1 1 0 1 1-1 1.73 12.8 12.8 0 0 1-2.11-1.5l-.69.69a1 1 0 0 1-1.41-1.41l.59-.59A12.5 12.5 0 0 1 8.1 12 13 13 0 0 1 6 9.59L4.7 8.3V12a1 1 0 0 1-2 0V5a1 1 0 0 1 1-1Z" />
                    <path d="M18 12h3a1 1 0 0 1 .94 1.34l-3 8A1 1 0 0 1 18 22h-3a1 1 0 0 1-.94-1.34l3-8A1 1 0 0 1 18 12Zm-1.38 8h1.76L18 17.62 16.62 20Z" />
                  </svg>
                </span>
                <div>
                  <h1 className="text-base font-semibold tracking-tight text-gray-900">
                    Add Language
                  </h1>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Provide the name and a short description.
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px w-full bg-gray-100" />

            {/* Content */}
            <div className="px-5 py-5">
              <div className="mb-3">
                <Title props={{ title: "Language Details" }} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  props={{
                    id: "title",
                    title: "Name",
                    type: "text",
                    required: true,
                    value: name ? name : "",
                    onChange: (e) => {
                      setName(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
                <TextArea
                  props={{
                    id: "description",
                    title: "Description",
                    type: "text",
                    required: true,
                    value: description ? description : "",
                    onChange: (e) => {
                      setDescription(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
              </div>
            </div>

            <div className="h-px w-full bg-gray-100" />

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 px-5 py-4">
              <ActionButton
                props={{
                  title: "Add Language",
                  error,
                  success,
                  message,
                  loading,
                  status,
                  onClick: () => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage("");
                  },
                }}
              />
            </div>
          </div>

          {/* Helper text */}
          <p className="mt-3 text-xs text-gray-500">
            You can edit this language later from the list view.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Add;
